import {
    assembleAssetTitle,
    CreateNewThreadDialog,
    Footer,
    joinDefinedStrings,
    MessageListEmptyState,
    ThreadSelection,
} from 'ComponentLibrary'
import { OrderSidebar } from 'ClientPortal/src/pages/Orders/OrderSidebar'
import { Stack } from '@mui/material'
import { isEmpty } from 'lodash'
import { useThreadListPageData } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/ThreadListPage.data'
import { useThreadSelection } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/useThreadSelection'
import { ThreadsMessageList } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/ThreadsMessageList'
import { useDeleteThread } from 'ClientPortal/src/api/Queries/threads'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export const ThreadListPage = () => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.threadListPage' })
    const locale = useCurrentResolvedLocale()
    const {
        orderOverview,
        threads,
        isThreadsPending,
        createMode,
        setCreateMode,
        users,
        isCreatingThread,
        onCreateThreadSubmit,
        pinThread,
        unpinThread,
    } = useThreadListPageData()

    const { onClickThread, selectedThread } = useThreadSelection({ threads })

    const assetTitle = assembleAssetTitle({ ...orderOverview?.address, customer_asset_reference: orderOverview?.customer_asset_reference })
    const orderTitle = joinDefinedStrings([assetTitle, orderOverview?.address.city], ', ')
    const userLinkCopy = useUserLinkCopy()
    const threadCopy = {
        noMessages: t('thread.noMessages'),
        deletedMessage: t('thread.deletedMessage'),
        createdBy: (userName: string) => t('thread.createdBy', { userName }),
        createdAutomatically: t('thread.createdAutomatically'),
    }
    const selectionCopy = {
        newThread: t('selection.newConversation'),
        item: {
            noTitle: t('selection.item.noTitle'),
            notifiedUsersTooltip: t('selection.item.notifiedUsersTooltip'),
            options: t('selection.item.options'),
            unpin: t('selection.item.unpin'),
            pin: t('selection.item.pin'),
            delete: t('selection.item.delete'),
            thread: threadCopy,
        },
    }
    const createDialogCopy = {
        title: t('createDialog.title'),
        create: t('createDialog.create'),
        cancel: t('createDialog.cancel'),
        topicLabel: t('createDialog.topicLabel'),
        participantsLabel: t('createDialog.participantsLabel'),
        defaultNotifiedUsers: t('createDialog.defaultNotifiedUsers'),
        yourself: t('createDialog.yourself'),
        creator: t('createDialog.creator'),
        observers: t('createDialog.observers'),
        assignees: t('createDialog.assignees'),
        observersDescriptionSuffix: t('createDialog.observersDescriptionSuffix'),
        orderDropdown: {
            loading: t('orderDropdown.loading'),
            label: t('orderDropdown.label'),
            noOptions: t('orderDropdown.noOptions'),
            valuationDate: t('orderDropdown.valuationDate'),
            orderDate: t('orderDropdown.orderDate'),
        },
    }
    const emptyStateCopy = {
        loadingTitle: t('emptyState.loadingTitle'),
        emptyTitle: t('emptyState.emptyTitle'),
        emptyDescriptionLineOne: t('emptyState.emptyDescriptionLineOne'),
        emptyDescriptionLineTwo: t('emptyState.emptyDescriptionLineTwo'),
        newThread: t('emptyState.newConversation'),
    }

    return (
        <Stack direction="row">
            <OrderSidebar />
            <ThreadSelection
                threads={threads}
                activeThreadId={selectedThread?.uuid}
                onClickThread={onClickThread}
                createMode={createMode}
                setCreateMode={setCreateMode}
                subtitle={orderTitle}
                footer={<Footer />}
                isLoadingThreads={isThreadsPending}
                pinThread={pinThread}
                unpinThread={unpinThread}
                useDeleteThread={useDeleteThread}
                copy={selectionCopy}
                userLinkCopy={userLinkCopy}
                locale={locale}
            />
            <CreateNewThreadDialog
                open={createMode}
                isCreatingThread={isCreatingThread}
                onSubmit={onCreateThreadSubmit}
                onClose={() => setCreateMode(false)}
                participantsProps={{ users }}
                copy={createDialogCopy}
                userLinkCopy={userLinkCopy}
                locale={locale}
            />
            {!isEmpty(threads) && selectedThread ? (
                <ThreadsMessageList thread={selectedThread} />
            ) : (
                <MessageListEmptyState isLoading={isThreadsPending} onNewThreadClick={() => setCreateMode(true)} copy={emptyStateCopy} />
            )}
        </Stack>
    )
}
